import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { InventoryCountRow, InventoryCountView, UnitDto, UnitKind } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { StatusChip } from '../../components/StatusChip';
import { formatDate } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useCarriers } from '../plans/lookups';
import { UnitDialog } from './UnitDialog';
import type { TileFilter } from './SummaryPanel';
import { api } from '../../lib/api';
import { unitExportUrl, useProductModelOptions, useUnits } from './api';
import { useFeatureReleased } from '../../release';
import { imeiOf, simOf, statusLabel, statusOptions, storeType } from './labels';

const PAGE_SIZES = [15, 30, 50, 100];
const NO_FILTERS = { carrierId: '', status: '', esim: '', modelId: '', q: '' };

/** Release switch of View Full Dashboard and the All Products filter. */
const FULL_DASHBOARD = 'inventory-full-dashboard';

export type View = UnitKind | InventoryCountView;
const VIEW_LABELS: Record<View, string> = {
  sim: 'SIM Inventory',
  device: 'Device / IMEI Inventory',
  product: 'SIM Count by Product',
  user: 'SIM Count Uploaded by User',
  age: 'SIM Count by Age',
};
const COUNT_TITLES: Record<InventoryCountView, string> = {
  product: 'SIM Count by Product',
  user: 'SIM Count Uploaded by User',
  age: 'SIM Count by Age',
};
const COUNT_COLUMNS: Record<InventoryCountView, Column<InventoryCountRow>[]> = {
  product: [
    { key: 'id', header: 'ID', render: (r) => r.key },
    { key: 'product', header: 'PRODUCT', render: (r) => r.label },
    { key: 'available', header: 'AVAILABLE', align: 'right', render: (r) => r.available },
    { key: 'used', header: 'USED', align: 'right', render: (r) => r.used },
    { key: 'bad', header: 'BAD', align: 'right', render: (r) => r.bad },
    { key: 'removed', header: 'REMOVED', align: 'right', render: (r) => r.removed },
    { key: 'total', header: 'TOTAL', align: 'right', render: (r) => r.total },
  ],
  user: [
    { key: 'user', header: 'User', render: (r) => r.label },
    { key: 'type', header: 'User Type', render: (r) => r.userType ?? '' },
    { key: 'free', header: 'Free', align: 'right', render: (r) => r.available },
    { key: 'used', header: 'Used', align: 'right', render: (r) => r.used },
    { key: 'bad', header: 'Bad', align: 'right', render: (r) => r.bad },
    { key: 'gone', header: 'Disconnected', align: 'right', render: (r) => r.disconnected ?? 0 },
  ],
  age: [
    { key: 'age', header: 'Age', render: (r) => r.label },
    { key: 'free', header: 'Free', align: 'right', render: (r) => r.available },
    { key: 'used', header: 'Used', align: 'right', render: (r) => r.used },
    { key: 'bad', header: 'Bad', align: 'right', render: (r) => r.bad },
  ],
};

/** A count tab of the new Inventory Dashboard: SIMs by product, by uploader or by age. */
function CountTable({ by }: { by: InventoryCountView }) {
  const counts = useQuery({
    queryKey: ['inventory', 'counts', by],
    queryFn: () => api.get<{ data: InventoryCountRow[] }>('/inventory/counts', { by }),
    select: (r) => r.data,
  });
  return (
    <DataTable
      label={COUNT_TITLES[by]}
      columns={COUNT_COLUMNS[by]}
      rows={counts.data}
      rowKey={(r) => r.key || r.label}
      loading={counts.isFetching}
      error={counts.error}
      onRetry={() => void counts.refetch()}
      emptyText="No record found"
    />
  );
}

/**
 * Unit list of the Inventory Dashboard: the tabs "SIM Inventory", "Device / IMEI Inventory",
 * "SIM Count by Product", "SIM Count Uploaded by User" and "SIM Count by Age" (walk of
 * 2026-10-15), filters, CSV export and the edit action. 15 rows per page, as in the reference.
 */
export function UnitList({
  view: controlled,
  onViewChange,
  views = ['sim', 'device', 'product', 'user', 'age'],
  filter,
}: {
  /** Set by the dashboard when a KPI tile opens a tab. */
  view?: View;
  onViewChange?: (view: View) => void;
  /** The tabs shown; the Full Inventory Dashboard's Quick Reports have three. */
  views?: View[];
  /** Set by a tile: the list opens filtered (the dashboard remounts the list for each tile). */
  filter?: TileFilter;
} = {}) {
  const featureOn = useFeatureReleased();
  const products = useProductModelOptions(featureOn(FULL_DASHBOARD));
  const [own, setOwn] = useState<View>('sim');
  const view = controlled ?? own;
  const setView = onViewChange ?? setOwn;
  const kind: UnitKind = view === 'device' ? 'device' : 'sim';
  const listing = view === 'sim' || view === 'device';
  const list = useListState(NO_FILTERS, 15, '-uploadedAt');
  const carriers = useCarriers();
  const units = useUnits({ ...list.params, kind });
  const [editing, setEditing] = useState<string | null>(null);
  const { apply } = list;
  useEffect(() => {
    if (filter) apply({ ...NO_FILTERS, ...filter });
  }, [filter, apply]);

  const switchView = (next: View | null) => {
    if (!next || next === view) return;
    // Each view has its own statuses and SIM-type filter, so a switch starts a fresh list.
    list.reset();
    setView(next);
  };

  // The identifier of the current view is the sortable column; the other one shows the paired unit.
  const identifierColumns: Column<UnitDto>[] = [
    {
      key: kind === 'sim' ? 'identifier' : 'pairedSim',
      header: 'SIM / ESN',
      sortable: kind === 'sim',
      nowrap: true,
      render: (u) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {simOf(u)}
          {u.kind === 'sim' && u.isEsim ? (
            <Chip size="small" variant="outlined" label="eSIM" />
          ) : null}
        </Box>
      ),
    },
    {
      key: kind === 'device' ? 'identifier' : 'pairedImei',
      header: 'IMEI',
      sortable: kind === 'device',
      nowrap: true,
      render: (u) => imeiOf(u),
    },
  ];

  // The device view leads with the IMEI and shows the model instead of the MDN, as the reference.
  const columns: Column<UnitDto>[] = [
    ...(kind === 'device'
      ? [
          { ...identifierColumns[1]!, header: 'IMEI / MEID' },
          { ...identifierColumns[0]!, header: 'SIM' },
          {
            key: 'model',
            header: 'Model',
            render: (u: UnitDto) => `${u.model.make} ${u.model.model}`.trim(),
          },
        ]
      : identifierColumns),
    { key: 'carrier', header: 'Carrier', render: (u) => u.carrier.code },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (u) => <StatusChip status={u.status} label={statusLabel(u.status)} />,
    },
    { key: 'storeType', header: 'Store Type', render: storeType },
    { key: 'storeId', header: 'Store ID', render: (u) => u.owner?.loginId ?? '' },
    ...(kind === 'sim' ? [{ key: 'mdn', header: 'MDN', render: (u: UnitDto) => u.mdn ?? '' }] : []),
    { key: 'po', header: 'PO', render: (u) => u.poNumber },
    {
      key: 'uploadedAt',
      header: 'Uploaded',
      sortable: true,
      nowrap: true,
      render: (u) => formatDate(u.uploadedAt),
    },
    {
      key: 'actions',
      header: 'Action',
      render: (u) => (
        <Tooltip title="Update Single Unit">
          <IconButton
            size="small"
            onClick={() => setEditing(u.id)}
            aria-label={`Update unit ${u.sim ?? u.imei}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box component="section" aria-label="Inventory list">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={view}
          onChange={(_e, next: View | null) => switchView(next)}
          aria-label="Inventory view"
        >
          {views.map((v) => (
            <ToggleButton key={v} value={v}>
              {VIEW_LABELS[v]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        {/* Downloads the rows that match the filters applied with Search, in the current sort order. */}
        {listing ? (
          <Button
            component="a"
            href={unitExportUrl({ ...list.applied, kind, sort: list.sort })}
            download
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Export
          </Button>
        ) : null}
      </Box>

      {listing ? null : <CountTable by={view as InventoryCountView} />}

      {listing ? (
        <>
          <FilterPanel onSearch={list.search} onReset={list.reset}>
            <TextField
              select
              label="Carrier"
              value={list.draft.carrierId}
              onChange={(e) => list.setField('carrierId', e.target.value)}
              error={carriers.isError}
              helperText={carriers.isError ? 'Could not load the carriers' : undefined}
            >
              <MenuItem value="">All Carriers</MenuItem>
              {(carriers.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={list.draft.status}
              onChange={(e) => list.setField('status', e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              {statusOptions(kind).map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            {kind === 'sim' ? (
              <TextField
                select
                label="Type"
                value={list.draft.esim}
                onChange={(e) => list.setField('esim', e.target.value)}
              >
                <MenuItem value="">All Types (eSIM/Physical)</MenuItem>
                <MenuItem value="true">eSIM Only</MenuItem>
                <MenuItem value="false">Physical SIM Only</MenuItem>
              </TextField>
            ) : null}
            {featureOn(FULL_DASHBOARD) ? (
              <TextField
                select
                label="Product"
                value={list.draft.modelId}
                onChange={(e) => list.setField('modelId', e.target.value)}
                error={products.isError}
                helperText={products.isError ? 'Could not load the products' : undefined}
              >
                <MenuItem value="">All Products</MenuItem>
                {(products.data ?? [])
                  .filter((m) => m.isSim === (kind === 'sim'))
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {`${m.make} ${m.model}`.trim()}
                    </MenuItem>
                  ))}
              </TextField>
            ) : null}
            <TextField
              label="Search"
              placeholder="Search IMEI / SIM / MDN / Store..."
              value={list.draft.q}
              onChange={(e) => list.setField('q', e.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </FilterPanel>

          <DataTable
            label={kind === 'sim' ? 'SIM Inventory' : 'Device / IMEI Inventory'}
            columns={columns}
            rows={units.data?.data}
            rowKey={(u) => u.id}
            loading={units.isFetching}
            error={units.error}
            onRetry={() => void units.refetch()}
            page={list.page}
            pageSize={list.pageSize}
            total={units.data?.total ?? 0}
            onPageChange={list.setPage}
            onPageSizeChange={list.setPageSize}
            pageSizeOptions={PAGE_SIZES}
            sort={list.sort}
            onSortChange={list.setSort}
          />
        </>
      ) : null}

      <UnitDialog unitId={editing} onClose={() => setEditing(null)} />
    </Box>
  );
}
